import { Icon } from "@iconify/react/dist/iconify.js";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState, useTransition } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { confirmPasswordRecovery } from "$/modules/auth/lib/auth";
import { AuthLayout } from "$/modules/auth/ui/auth-layout";
import { PasswordInput } from "$/modules/auth/ui/password-input";
import { Button } from "$/modules/shared/ui/button";
import { Spinner } from "$/modules/shared/ui/spinner";

type RecoverySearch = {
	userId?: string;
	secret?: string;
};

export const Route = createFileRoute("/$locale/recovery/")({
	component: RecoveryPage,
	validateSearch: (search: Record<string, unknown>): RecoverySearch => ({
		userId: typeof search.userId === "string" ? search.userId : undefined,
		secret: typeof search.secret === "string" ? search.secret : undefined,
	}),
});

function RecoveryPage() {
	const [password, setPassword] = useState("");
	const [confirm, setConfirm] = useState("");
	const t = useTranslations();
	const { locale } = Route.useParams();
	const { userId, secret } = Route.useSearch();
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate();

	const hasToken = Boolean(userId && secret);

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		if (password !== confirm) {
			toast.error(t("auth.passwordMismatch"), {});
			return;
		}

		if (!userId || !secret) return;

		startTransition(async () => {
			const response = await confirmPasswordRecovery(userId, secret, password);
			if (!response.success) {
				toast.error(response.message, {});
				return;
			}
			toast.success(t("auth.recoverySuccess"), {});
			void navigate({ to: "/$locale/signin", params: { locale } });
		});
	};

	return (
		<AuthLayout
			locale={locale}
			title={t("auth.recoveryTitle")}
			subtitle={hasToken ? t("auth.recoveryDescription") : undefined}
		>
			{hasToken ? (
				<form onSubmit={handleSubmit} className="space-y-4">
					<PasswordInput
						label={t("auth.newPassword")}
						placeholder={t("passwordPlaceholder")}
						value={password}
						onValueChange={setPassword}
						autoComplete="new-password"
						isRequired
						minLength={8}
					/>
					<PasswordInput
						label={t("auth.confirmPassword")}
						placeholder={t("passwordPlaceholder")}
						value={confirm}
						onValueChange={setConfirm}
						autoComplete="new-password"
						isRequired
						minLength={8}
					/>
					<Button
						type="submit"
						size="lg"
						className="w-full font-semibold"
						disabled={isPending}
						variant="default"
					>
						{isPending && <Spinner data-icon="inline-start" />}
						{t("auth.setNewPassword")}
					</Button>
				</form>
			) : (
				<div className="flex flex-col items-center gap-3 py-4 text-center">
					<span className="flex size-12 items-center justify-center rounded-full bg-danger/10 text-danger">
						<Icon icon="lucide:link-2-off" className="size-6" />
					</span>
					<p className="text-foreground-600">{t("auth.recoveryInvalid")}</p>
					<Link
						to="/$locale/forgot-password"
						params={{ locale }}
						className="font-semibold text-primary hover:underline"
					>
						{t("auth.forgotTitle")}
					</Link>
				</div>
			)}
		</AuthLayout>
	);
}
